import { useQuery } from "@tanstack/react-query";
import { env } from "../lib/env";
import { supabase } from "../lib/supabase";

type ViewValue = string | number | boolean | null;

async function queryView<T extends Record<string, ViewValue>>(view: string): Promise<T[]> {
  const { data, error } = await supabase.from(view).select("*");
  if (error) throw error;
  return (data ?? []) as T[];
}

export interface Customer360Row {
  id: string;
  displayName: string;
  kind: string;
  identifier: string;
  contact: string;
  financialStatus: string;
  riskLevel: string;
  contracts: number;
  subscriptions: number;
  agreements: number;
  openInvoices: number;
  outstandingCents: string;
  openTasks: number;
  lastEventAt?: string;
}

const customerMock: Customer360Row[] = [
  {
    id: "c360-1",
    displayName: "Família Oliveira",
    kind: "HOUSEHOLD",
    identifier: "***0182",
    contact: "(41) 9****-0182",
    financialStatus: "CURRENT",
    riskLevel: "LOW",
    contracts: 2,
    subscriptions: 2,
    agreements: 0,
    openInvoices: 1,
    outstandingCents: "9890",
    openTasks: 1,
    lastEventAt: "2026-09-20T14:30:00Z",
  },
  {
    id: "c360-2",
    displayName: "Marcos Transportes Ltda",
    kind: "LEGAL_ENTITY",
    identifier: "***7721",
    contact: "(41) 3***-2200",
    financialStatus: "OVERDUE",
    riskLevel: "MEDIUM",
    contracts: 1,
    subscriptions: 0,
    agreements: 3,
    openInvoices: 2,
    outstandingCents: "560000",
    openTasks: 2,
    lastEventAt: "2026-09-20T13:10:00Z",
  },
];

export function useCustomer360Directory() {
  return useQuery({
    queryKey: ["customer-360"],
    queryFn: async (): Promise<Customer360Row[]> => {
      if (env.useMocks) return customerMock;
      const rows = await queryView<Record<string, ViewValue>>("app_customer_360_directory");
      return rows.map((row) => ({
        id: String(row.id),
        displayName: String(row.display_name),
        kind: String(row.account_kind),
        identifier: row.identifier_last4 ? `***${String(row.identifier_last4)}` : "—",
        contact: String(row.primary_contact_masked ?? "—"),
        financialStatus: String(row.financial_status),
        riskLevel: String(row.risk_level),
        contracts: Number(row.contract_count ?? 0),
        subscriptions: Number(row.subscription_count ?? 0),
        agreements: Number(row.asset_agreement_count ?? 0),
        openInvoices: Number(row.open_invoice_count ?? 0),
        outstandingCents: String(row.outstanding_cents ?? 0),
        openTasks: Number(row.open_task_count ?? 0),
        ...(row.last_event_at ? { lastEventAt: String(row.last_event_at) } : {}),
      }));
    },
  });
}

export interface WorkflowQueueRow {
  id: string;
  workflow: string;
  entityType: string;
  state: string;
  status: string;
  pending: number;
  failed: number;
  updatedAt: string;
}
export function useWorkflowQueue() {
  return useQuery({
    queryKey: ["workflow-queue"],
    queryFn: async (): Promise<WorkflowQueueRow[]> => {
      if (env.useMocks)
        return [
          {
            id: "wf-1",
            workflow: "Ativação de plano",
            entityType: "SUBSCRIPTION",
            state: "AWAITING_PAYMENT",
            status: "ACTIVE",
            pending: 1,
            failed: 0,
            updatedAt: "20/09/2026 14:12",
          },
          {
            id: "wf-2",
            workflow: "Venda parcelada de veículo",
            entityType: "ASSET_AGREEMENT",
            state: "AWAITING_SIGNATURE",
            status: "ACTIVE",
            pending: 0,
            failed: 0,
            updatedAt: "20/09/2026 13:52",
          },
          {
            id: "wf-3",
            workflow: "Provisioning MobiMedical",
            entityType: "SUBSCRIPTION",
            state: "MANUAL_REVIEW",
            status: "ACTIVE",
            pending: 0,
            failed: 1,
            updatedAt: "20/09/2026 11:02",
          },
        ];
      const rows = await queryView<Record<string, ViewValue>>("app_workflow_queue");
      return rows.map((row) => ({
        id: String(row.id),
        workflow: String(row.workflow_name),
        entityType: String(row.entity_type),
        state: String(row.current_state_code),
        status: String(row.status),
        pending: Number(row.pending_actions ?? 0),
        failed: Number(row.failed_actions ?? 0),
        updatedAt: new Date(String(row.updated_at)).toLocaleString("pt-BR"),
      }));
    },
  });
}

export interface NotificationRow {
  id: string;
  customer: string;
  channel: string;
  destination: string;
  subject: string;
  status: string;
  scheduledAt: string;
  attempts: number;
}
export function useNotifications() {
  return useQuery({
    queryKey: ["notifications"],
    queryFn: async (): Promise<NotificationRow[]> => {
      if (env.useMocks)
        return [
          {
            id: "n-1",
            customer: "Carlos Oliveira",
            channel: "WHATSAPP",
            destination: "***0182",
            subject: "Pagamento confirmado",
            status: "DELIVERED",
            scheduledAt: "20/09/2026 14:22",
            attempts: 1,
          },
          {
            id: "n-2",
            customer: "Marcos Transportes Ltda",
            channel: "EMAIL",
            destination: "m***@empresa.com",
            subject: "Cobrança vencida",
            status: "QUEUED",
            scheduledAt: "20/09/2026 15:00",
            attempts: 0,
          },
        ];
      const rows = await queryView<Record<string, ViewValue>>("app_notification_directory");
      return rows.map((row) => ({
        id: String(row.id),
        customer: String(row.customer ?? "—"),
        channel: String(row.channel),
        destination: String(row.destination_masked),
        subject: String(row.subject ?? "Notificação"),
        status: String(row.status),
        scheduledAt: new Date(String(row.scheduled_at)).toLocaleString("pt-BR"),
        attempts: Number(row.attempt_count ?? 0),
      }));
    },
  });
}

export interface DocumentTemplateRow {
  id: string;
  name: string;
  type: string;
  version: number;
  status: string;
  approval: string;
  approvals: string;
}
export function useDocumentTemplates() {
  return useQuery({
    queryKey: ["document-templates"],
    queryFn: async (): Promise<DocumentTemplateRow[]> => {
      if (env.useMocks)
        return [
          {
            id: "d-1",
            name: "Termo funerário",
            type: "FUNERAL_TERM",
            version: 1,
            status: "ACTIVE",
            approval: "IN_REVIEW",
            approvals: "1/3",
          },
          {
            id: "d-2",
            name: "Venda parcelada de veículo",
            type: "VEHICLE_SALE",
            version: 1,
            status: "DRAFT",
            approval: "DRAFT",
            approvals: "0/3",
          },
        ];
      const rows = await queryView<Record<string, ViewValue>>("app_document_template_directory");
      return rows.map((row) => ({
        id: String(row.id),
        name: String(row.name),
        type: String(row.document_type),
        version: Number(row.version),
        status: String(row.status),
        approval: String(row.approval_status),
        approvals: `${Number(row.approved_count ?? 0)}/${Number(row.approval_count ?? 0)}`,
      }));
    },
  });
}

export interface ImportProfileRow {
  id: string;
  name: string;
  entityType: string;
  format: string;
  status: string;
  duplicatePolicy: string;
  mappings: number;
}
export function useImportProfiles() {
  return useQuery({
    queryKey: ["import-profiles"],
    queryFn: async (): Promise<ImportProfileRow[]> => {
      if (env.useMocks)
        return [
          {
            id: "i-1",
            name: "Clientes PF/PJ",
            entityType: "CUSTOMER",
            format: "CSV",
            status: "ACTIVE",
            duplicatePolicy: "REVIEW",
            mappings: 9,
          },
          {
            id: "i-2",
            name: "Vendas parceladas Celomar",
            entityType: "VEHICLE_SALE",
            format: "XLSX",
            status: "ACTIVE",
            duplicatePolicy: "REVIEW",
            mappings: 15,
          },
        ];
      const rows = await queryView<Record<string, ViewValue>>("app_import_profile_directory");
      return rows.map((row) => ({
        id: String(row.id),
        name: String(row.name),
        entityType: String(row.target_entity_type),
        format: String(row.source_format),
        status: String(row.status),
        duplicatePolicy: String(row.duplicate_policy),
        mappings: Number(row.mapping_count ?? 0),
      }));
    },
  });
}

export interface TenantHealthRow {
  tenantId: string;
  slug: string;
  status: string;
  production: boolean;
  healthy: number;
  failed: number;
  inbox: number;
  outbox: number;
  deadLetters: number;
  reconciliation: number;
  criticalIncidents: number;
}
export function useTenantHealth() {
  return useQuery({
    queryKey: ["tenant-health"],
    queryFn: async (): Promise<TenantHealthRow[]> => {
      if (env.useMocks)
        return [
          {
            tenantId: "t-1",
            slug: "luto-cruzeiro",
            status: "PILOT",
            production: false,
            healthy: 4,
            failed: 0,
            inbox: 0,
            outbox: 1,
            deadLetters: 0,
            reconciliation: 0,
            criticalIncidents: 0,
          },
          {
            tenantId: "t-2",
            slug: "celomar-patrimonio",
            status: "PILOT",
            production: false,
            healthy: 2,
            failed: 0,
            inbox: 0,
            outbox: 0,
            deadLetters: 0,
            reconciliation: 1,
            criticalIncidents: 0,
          },
        ];
      const rows = await queryView<Record<string, ViewValue>>("platform_tenant_health");
      return rows.map((row) => ({
        tenantId: String(row.tenant_id),
        slug: String(row.slug),
        status: String(row.status),
        production: Boolean(row.production_enabled),
        healthy: Number(row.healthy_integrations ?? 0),
        failed: Number(row.failed_integrations ?? 0),
        inbox: Number(row.inbox_pending ?? 0),
        outbox: Number(row.outbox_pending ?? 0),
        deadLetters: Number(row.dead_letters ?? 0),
        reconciliation: Number(row.reconciliation_issues ?? 0),
        criticalIncidents: Number(row.critical_incidents ?? 0),
      }));
    },
  });
}
